import { formatSupportDays, supportTimezoneCity } from '../helpers/supportHours'

import { SxProps, Theme } from '@mui/material'
import { FC, useEffect } from 'react'
import { GRAY_500 } from 'constants/styling/theme'
import Typography from '@mui/material/Typography'
import { logger } from 'utils/logging'
import { useGetSupportHours } from 'dataQueries/conversations.query'
import { useSupportHoursLocale } from './useSupportHoursLocale'
import { useTranslation } from 'react-i18next'

interface Props {
  centered?: boolean
  sx?: SxProps<Theme>
}

const reportedUnusableDays = new Set<string>()

export const SupportHoursSummary: FC<Props> = ({ centered = false, sx }) => {
  const { t } = useTranslation(['chat'])
  const locale = useSupportHoursLocale()
  const { data: hours } = useGetSupportHours()

  const days = hours ? formatSupportDays(hours.days, locale) : ''
  const isUnusable = !!hours && !days

  useEffect(() => {
    if (!isUnusable || !hours) return
    const daysKey = JSON.stringify(hours.days ?? null)
    if (reportedUnusableDays.has(daysKey)) return
    reportedUnusableDays.add(daysKey)
    logger.warn('ChatWidget: support hours carry no usable day, hiding the hours summary', undefined, {
      context: { component: 'SupportHoursSummary', action: 'formatSupportDays', metadata: { days: hours.days ?? null } },
    })
  }, [isUnusable, hours])

  if (!hours || !days) return null

  return (
    <Typography
      variant="text-xs"
      color={GRAY_500}
      noWrap
      textAlign={centered ? 'center' : undefined}
      data-testid="support-hours-summary"
      sx={sx}
    >
      {t('chat:support_hours.summary', {
        days,
        opensAt: hours.opensAt,
        closesAt: hours.closesAt,
        city: supportTimezoneCity(hours.timezone),
      })}
    </Typography>
  )
}
